import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import {
    ArrowDownLeft,
    ArrowUpRight,
    ChevronDown,
    Clock,
    Download,
    FileText,
    Forward,
    History,
    List,
    Lock,
    Mail,
    MessageSquare,
    Paperclip,
    Pencil,
    Reply,
    Settings2,
    Sparkles,
    StickyNote,
} from "lucide-react";
import type { ComponentType } from "react";
import type { RecordData } from "../core";
import { useRuntime } from "../react/runtime/RuntimeContext";
import { useLocale } from "../react/providers/LocaleProvider";
import { useEvent } from "../react/hooks/useEvent";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { fmtDateTime } from "../widgets/builtins";
import { resolveUserLabels } from "./chatterUserNames";
import { fmtSize, isImage, isInlineViewable } from "./RecordDocumentsDrawer";
import { RecordComposer } from "./RecordComposer";
import type { TimelineFilter } from "./RecordStrip";

// Unified record timeline (communication Enh 03 Slice 4) — the frozen Option I
// bottom surface: ONE chronological, day-grouped, typed, filterable feed below
// the form sheet.
//
// Entry sources:
//   - communication.message rows (message / note / email / activity /
//     notification / tracking) via /api/chatter — tracking rows are the
//     persisted field-change audit entries posted by the Slice 4a hooks
//   - record_attachment rows merged client-side as "file" entries (only when
//     the <Attachments/> component survived gating)
//
// Filter pills render only for entry kinds whose backing <activity> component
// survived Slice 1's gating, so a partial declaration shows a partial pill
// row by construction. Live refresh: `record.saved` for this record (field
// changes made here), plus the strip's mutation epoch via `refreshSignal`.

export type TimelineKind =
    | "message"
    | "note"
    | "email"
    | "activity"
    | "notification"
    | "tracking"
    | "file";

export interface TimelineAttachment {
    uuid?: string;
    name?: string;
    size?: number | null;
    mimetype?: string;
}

export interface TimelineEntry {
    id: string;
    kind: TimelineKind;
    /** ISO timestamp used for ordering + day grouping. */
    ts: string;
    authorLabel: string;
    body: string;
    /** Display verb overriding the kind's default line (lifecycle entries). */
    verb?: string;
    fileName?: string;
    fileSize?: string;
    attachmentUuid?: string;
    mimetype?: string;
    // Rich chatter (Enh 04 split Discuss panel) — email envelope + reply thread.
    direction?: "in" | "out" | null;
    subject?: string;
    fromLabel?: string;
    toLabel?: string;
    ccLabel?: string;
    attachments?: TimelineAttachment[];
    replies?: TimelineEntry[];
}

interface PillDef {
    key: TimelineFilter;
    label: string;
    kinds: TimelineKind[] | null;
    /** <activity> component this pill needs; null → always available. */
    needs: string | null;
    /** Restrict to one email direction (Enh 04 Incoming-Emails filter). */
    dir?: "in" | "out";
    /** Filter-chip icon (mockup `.rc-chip` icons). */
    icon: ComponentType<{ size?: number }>;
}

export const TIMELINE_PILLS: PillDef[] = [
    { key: "all", label: "All", kinds: null, needs: null, icon: List },
    { key: "comment", label: "Comments", kinds: ["message", "note"], needs: "MessageConversation", icon: MessageSquare },
    { key: "email", label: "Incoming Emails", kinds: ["email"], needs: "MessageConversation", dir: "in", icon: ArrowDownLeft },
    { key: "change", label: "Changes", kinds: ["tracking", "notification"], needs: "MessageConversation", icon: Pencil },
    { key: "file", label: "Files", kinds: ["file"], needs: "Attachments", icon: Paperclip },
    { key: "task", label: "Activities", kinds: ["activity"], needs: "ScheduledActivity", icon: Clock },
];

const KIND_LINES: Record<TimelineKind, string> = {
    message: "commented",
    note: "left an internal note",
    email: "sent an email",
    activity: "completed an activity",
    notification: "system notification",
    tracking: "changed",
    file: "attached a file",
};

/** Split a message body into text + @mention tokens (same token grammar as
    the server-side parser). Emails display as their local part. */
export type BodyPart = { kind: "text"; text: string } | { kind: "mention"; token: string; display: string };

export function splitMentionTokens(body: string): BodyPart[] {
    const out: BodyPart[] = [];
    const re = /@([^\s,;:()<>\[\]{}"']+)/g;
    let last = 0;
    for (const m of body.matchAll(re)) {
        const idx = m.index ?? 0;
        if (idx > last) out.push({ kind: "text", text: body.slice(last, idx) });
        const token = m[1].replace(/[.,!?]+$/, "");
        const consumed = `@${token}`;
        out.push({ kind: "mention", token, display: token.includes("@") ? token.split("@")[0] : token });
        last = idx + consumed.length;
    }
    if (last < body.length) out.push({ kind: "text", text: body.slice(last) });
    return out;
}

function BodyWithMentions({ body }: { body: string }): ReactNode {
    const parts = splitMentionTokens(body);
    return (
        <>
            {parts.map((part, i) =>
                part.kind === "text" ? (
                    <span key={i}>{part.text}</span>
                ) : (
                    <span className="record-timeline__mention" key={i} title={`@${part.token}`}>
                        @{part.display}
                    </span>
                ),
            )}
        </>
    );
}

/** Two-letter avatar initials from a display label. */
function initials(label: string): string {
    const parts = label.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "?";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const ASK_AI_CHIPS = [
    "Read attachments & extract data",
    "Process PO → cargo & charges",
    "Draft a reply",
    "Summarize",
    "Create task",
];

// Tag metadata per message kind (mockup `.msg__tag--*`).
const MSG_TAG: Record<string, { label: string; cls: string; Icon: ComponentType<{ size?: number }> }> = {
    note: { label: "Note", cls: "note", Icon: StickyNote },
    message: { label: "Comment", cls: "comment", Icon: MessageSquare },
    "email-in": { label: "Email in", cls: "in", Icon: ArrowDownLeft },
    "email-out": { label: "Email out", cls: "out", Icon: ArrowUpRight },
};

/** Rich message card (Enh 04 split Discuss panel) — header (who · tag · time) +
    body for notes/comments; emails add the envelope + attachments + Reply/
    Forward/Ask-AI + a reply sub-thread (top-5 + load-more). Faithful to the
    frozen mockup's `.msg--note` / `.msg--in` / `.msg--out`. */
function MsgCard({ entry, timezone }: { entry: TimelineEntry; timezone: string }): ReactNode {
    const [showAllReplies, setShowAllReplies] = useState(false);
    const [askOpen, setAskOpen] = useState(false);
    const isEmail = entry.kind === "email";
    const tagKey = isEmail ? (entry.direction === "in" ? "email-in" : "email-out") : entry.kind;
    const tag = MSG_TAG[tagKey] ?? MSG_TAG.message;
    const replies = entry.replies ?? [];
    const shown = showAllReplies ? replies : replies.slice(0, 5);
    const atts = entry.attachments ?? [];
    return (
        <div className={`chatter-msg chatter-msg--${tag.cls}`}>
            <div className="chatter-msg__head">
                <span className="chatter-msg__who">{entry.authorLabel}</span>
                <span className={`chatter-msg__tag chatter-msg__tag--${tag.cls}`}>
                    <tag.Icon size={12} aria-hidden="true" /> {tag.label}
                </span>
                <span className="chatter-msg__time">{entry.ts ? fmtDateTime(entry.ts, timezone) : ""}</span>
            </div>
            {isEmail && entry.subject ? <div className="chatter-msg__subject">{entry.subject}</div> : null}
            {isEmail && (entry.fromLabel || entry.toLabel || entry.ccLabel) ? (
                <div className="chatter-msg__meta">
                    {entry.fromLabel ? <span><b>From:</b> {entry.fromLabel}</span> : null}
                    {entry.toLabel ? <span><b>To:</b> {entry.toLabel}</span> : null}
                    {entry.ccLabel ? <span><b>Cc:</b> {entry.ccLabel}</span> : null}
                </div>
            ) : null}
            {entry.body ? (
                <div className="chatter-msg__body"><BodyWithMentions body={entry.body} /></div>
            ) : null}
            {isEmail && atts.length ? (
                <div className="chatter-msg__atts">
                    {atts.map((a, i) => (
                        <span className="chatter-msg__att" key={a.uuid ?? i}>
                            <Paperclip size={13} aria-hidden="true" />
                            {a.name}
                            {a.size ? ` · ${fmtSize(a.size)}` : ""}
                        </span>
                    ))}
                </div>
            ) : null}
            {isEmail ? (
            <div className="chatter-msg__actions">
                <button type="button" className="chatter-msg__btn"><Reply size={13} /> Reply</button>
                <button type="button" className="chatter-msg__btn"><Forward size={13} /> Forward</button>
                <span className="chatter-msg__spacer" />
                <button type="button" className="chatter-msg__ai" onClick={() => setAskOpen((v) => !v)}>
                    <Sparkles size={13} /> Ask AI
                </button>
            </div>
            ) : null}
            {isEmail && askOpen ? (
                <div className="chatter-ai">
                    <div className="chatter-ai__head"><Sparkles size={13} /> Ask the assistant about this email</div>
                    <div className="chatter-ai__chips">
                        {ASK_AI_CHIPS.map((c) => (
                            <button type="button" className="chatter-ai__chip" key={c}>{c}</button>
                        ))}
                    </div>
                </div>
            ) : null}
            {isEmail && replies.length ? (
                <div className="chatter-replies">
                    <div className="chatter-replies__label">Replies · {shown.length} of {replies.length}</div>
                    {shown.map((r) => (
                        <div className="chatter-reply" key={r.id}>
                            <span className="chatter-reply__av">{initials(r.authorLabel)}</span>
                            <div className="chatter-reply__body">
                                <div className="chatter-reply__head">
                                    <span className="chatter-reply__who">{r.authorLabel}</span>
                                    <span className="chatter-reply__time">{r.ts ? fmtDateTime(r.ts, timezone) : ""}</span>
                                </div>
                                <div className="chatter-reply__text">{r.body}</div>
                            </div>
                        </div>
                    ))}
                    {replies.length > 5 && !showAllReplies ? (
                        <button type="button" className="chatter-loadmore" onClick={() => setShowAllReplies(true)}>
                            <ChevronDown size={13} /> Load {replies.length - 5} more replies
                        </button>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}

/** Lifecycle tracking bodies → display verbs (no change-line rendering). */
export function lifecycleVerb(body: string): string | null {
    if (body.includes(" → ") || body.includes("\n")) return null; // field diff
    if (body === "Record created") return "created this record";
    if (body === "Record updated") return "updated this record";
    if (body.startsWith("Workflow:")) return body.slice("Workflow:".length).trim();
    if (body.startsWith("Approval:")) return `approval ${body.slice("Approval:".length).trim()}`;
    return null;
}

/** Map one communication.message wire row into a timeline entry. */
export function mapMessageRow(row: RecordData): TimelineEntry {
    const kind = String(row.message_type ?? "message") as TimelineKind;
    const body = typeof row.body === "string" ? row.body : "";
    const resolvedKind = (["message", "note", "email", "activity", "notification", "tracking"] as string[]).includes(kind)
        ? (kind as TimelineKind)
        : "message";
    const verb = resolvedKind === "tracking" ? lifecycleVerb(body) : null;
    const str = (v: unknown): string | undefined => (typeof v === "string" && v ? v : undefined);
    const rawAtts = Array.isArray(row.attachments) ? (row.attachments as RecordData[]) : [];
    const rawReplies = Array.isArray(row.replies) ? (row.replies as RecordData[]) : [];
    const dir = row.direction === "in" || row.direction === "out" ? row.direction : null;
    return {
        id: typeof row.record_uuid === "string" ? row.record_uuid : String(row.id ?? ""),
        kind: resolvedKind,
        ts: typeof row.sent_at === "string" ? row.sent_at : "",
        authorLabel: typeof row.author_id === "string" ? row.author_id : "System",
        // Lifecycle entries render their whole body as the verb line.
        body: verb ? "" : body,
        verb: verb ?? undefined,
        direction: dir,
        subject: str(row.subject),
        fromLabel: str(row.from_label),
        toLabel: str(row.to_label),
        ccLabel: str(row.cc_label),
        attachments: rawAtts.map((a) => ({
            uuid: str(a.uuid),
            name: str(a.name),
            size: typeof a.size === "number" ? a.size : null,
            mimetype: str(a.mimetype),
        })),
        replies: rawReplies.map(mapMessageRow),
    };
}

/** Map one ir.attachment wire row into a "file" timeline entry. */
export function mapAttachmentRow(row: RecordData): TimelineEntry {
    const uuid = typeof row.record_uuid === "string" ? row.record_uuid : String(row.id ?? "");
    return {
        id: `file-${uuid}`,
        kind: "file",
        ts: typeof row.uploaded_at_utc === "string" ? row.uploaded_at_utc : "",
        authorLabel: typeof row.owner_id === "string" ? row.owner_id : "System",
        body: "",
        fileName: typeof row.name === "string" ? row.name : "(unnamed file)",
        fileSize: fmtSize(typeof row.size_bytes === "number" ? row.size_bytes : null),
        attachmentUuid: uuid,
        mimetype: typeof row.mimetype === "string" ? row.mimetype : "",
    };
}

/** Day bucket label for grouping — Today / Yesterday / locale date. */
export function dayLabel(ts: string, now: Date = new Date()): string {
    if (!ts) return "Earlier";
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return "Earlier";
    const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
    const diffDays = Math.round((startOf(now) - startOf(d)) / 86_400_000);
    if (diffDays <= 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

function nodeFor(kind: TimelineKind): ReactNode {
    switch (kind) {
        case "note":
            return <Lock size={13} />;
        case "email":
            return <Mail size={13} />;
        case "activity":
            return <Clock size={13} />;
        case "tracking":
            return <Pencil size={13} />;
        case "notification":
            return <Settings2 size={13} />;
        case "file":
            return <FileText size={13} />;
        default:
            return <MessageSquare size={13} />;
    }
}

interface RecordTimelineViewProps {
    components: string[];
    entries: TimelineEntry[];
    /** attachmentUuid → object URL for image-file card thumbnails. */
    thumbs?: Record<string, string>;
    loading: boolean;
    activeFilter: TimelineFilter;
    onFilterChange: (filter: TimelineFilter) => void;
    timezone: string;
    /** File card click — container previews viewable types, downloads the rest. */
    onFileOpen?: (entry: TimelineEntry) => void;
}

export function RecordTimelineView({
    components,
    entries,
    thumbs = {},
    loading,
    activeFilter,
    onFilterChange,
    timezone,
    onFileOpen,
}: RecordTimelineViewProps): ReactNode {
    const pills = TIMELINE_PILLS.filter((p) => p.needs === null || components.includes(p.needs));
    const activePill = TIMELINE_PILLS.find((p) => p.key === activeFilter);
    const matchesPill = (e: TimelineEntry, p: PillDef | undefined): boolean => {
        if (!p) return true;
        if (p.kinds && !p.kinds.includes(e.kind)) return false;
        if (p.dir && e.direction !== p.dir) return false;
        return true;
    };
    const visible = activePill && activePill.key !== "all" ? entries.filter((e) => matchesPill(e, activePill)) : entries;

    // Group by day, newest day first; entries inside a day newest first.
    const groups: Array<{ label: string; items: TimelineEntry[] }> = [];
    for (const entry of visible) {
        const label = dayLabel(entry.ts);
        const last = groups[groups.length - 1];
        if (last && last.label === label) last.items.push(entry);
        else groups.push({ label, items: [entry] });
    }

    const countFor = (pill: PillDef) =>
        pill.kinds === null ? entries.length : entries.filter((e) => matchesPill(e, pill)).length;

    return (
        <div className="record-timeline" data-testid="record-timeline">
            <div className="record-timeline__toolbar">
                <h2 className="record-timeline__title">
                    <History size={14} aria-hidden="true" />
                    Timeline
                    <span className="record-timeline__count">
                        · {visible.length} {visible.length === 1 ? "entry" : "entries"}
                    </span>
                </h2>
                <div className="record-timeline__pills" role="group" aria-label="Timeline filters">
                    {pills.map((pill) => (
                        <button
                            type="button"
                            key={pill.key}
                            className={`record-timeline__pill${activeFilter === pill.key ? " is-active" : ""}`}
                            onClick={() => onFilterChange(pill.key)}
                        >
                            <pill.icon size={13} />
                            {pill.label}
                            <span className="record-timeline__pill-n">{countFor(pill)}</span>
                        </button>
                    ))}
                </div>
            </div>

            <div className="record-timeline__body">
                {loading ? (
                    <p className="record-timeline__empty">Loading…</p>
                ) : visible.length === 0 ? (
                    <p className="record-timeline__empty">Nothing here yet.</p>
                ) : (
                    groups.map((group) => (
                        <div className="record-timeline__day" key={group.label}>
                            <div className="record-timeline__day-chip">{group.label}</div>
                            <div className="record-timeline__rail">
                                {group.items.map((entry) => (
                                    <div className="record-timeline__item" key={entry.id} data-kind={entry.kind} data-viewid="ede-chatter-message" data-message-id={entry.id}>
                                        <span className={`record-timeline__node record-timeline__node--${entry.kind}`}>
                                            {entry.kind === "note" || entry.kind === "message" || entry.kind === "email"
                                                ? initials(entry.authorLabel)
                                                : nodeFor(entry.kind)}
                                        </span>
                                        <div className="record-timeline__content">
                                            {entry.kind === "note" || entry.kind === "message" || entry.kind === "email" ? null : (
                                                <div className="record-timeline__line">
                                                    <b>{entry.authorLabel}</b> {entry.verb ?? KIND_LINES[entry.kind]}
                                                    <time>{entry.ts ? fmtDateTime(entry.ts, timezone) : ""}</time>
                                                </div>
                                            )}
                                            {entry.kind === "tracking" && entry.body ? (
                                                <div className="record-timeline__changes">
                                                    {entry.body.split("\n").map((line, i) => (
                                                        <div className="record-timeline__change" key={i}>{line}</div>
                                                    ))}
                                                </div>
                                            ) : entry.kind === "file" ? (
                                                <button
                                                    type="button"
                                                    className="record-timeline__file-card"
                                                    onClick={() => onFileOpen?.(entry)}
                                                    title={isInlineViewable(entry.mimetype ?? "") ? "Preview" : "Download"}
                                                >
                                                    <span className="record-timeline__file-visual">
                                                        {entry.attachmentUuid && thumbs[entry.attachmentUuid] ? (
                                                            <img src={thumbs[entry.attachmentUuid]} alt={entry.fileName} />
                                                        ) : (
                                                            <FileText size={22} aria-hidden="true" />
                                                        )}
                                                    </span>
                                                    <span className="record-timeline__file-meta">
                                                        <span className="record-timeline__file-name">{entry.fileName}</span>
                                                        <span className="record-timeline__file-sub">
                                                            {[entry.fileSize, isInlineViewable(entry.mimetype ?? "") ? "click to preview" : "click to download"]
                                                                .filter(Boolean)
                                                                .join(" · ")}
                                                        </span>
                                                    </span>
                                                </button>
                                            ) : entry.kind === "note" || entry.kind === "message" || entry.kind === "email" ? (
                                                <MsgCard entry={entry} timezone={timezone} />
                                            ) : entry.body ? (
                                                <div className="record-timeline__speech">
                                                    <BodyWithMentions body={entry.body} />
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}

interface RecordTimelineProps {
    components: string[];
    modelKey: string;
    recordUuid?: string;
    /** Form field defs — the composer's @mention picker reads res.partner
        reference fields off the open record. */
    fieldDefs?: import("../core").FieldDef[];
    /** Bumped externally (strip mutations) to refetch. */
    refreshSignal?: number;
    /** Strip chip jump — set filter + scroll the timeline into view. */
    jumpSignal?: { filter: TimelineFilter; n: number } | null;
    /** Bubbled when the composer posts — lets the strip refresh counts. */
    onMutated?: () => void;
}

const TIMELINE_KINDS_NEED = ["MessageConversation", "ScheduledActivity", "Attachments"];

export function RecordTimeline({
    components,
    modelKey,
    recordUuid,
    fieldDefs,
    refreshSignal,
    jumpSignal,
    onMutated,
}: RecordTimelineProps): ReactNode {
    const runtime = useRuntime();
    const locale = useLocale();
    const rootRef = useRef<HTMLDivElement>(null);
    const [entries, setEntries] = useState<TimelineEntry[]>([]);
    const [loading, setLoading] = useState(false);
    const [activeFilter, setActiveFilter] = useState<TimelineFilter>("all");
    const [epoch, setEpoch] = useState(0);
    const [thumbs, setThumbs] = useState<Record<string, string>>({});
    const thumbsRef = useRef<Record<string, string>>({});
    const [preview, setPreview] = useState<{ name: string; url: string; mimetype: string } | null>(null);

    const eligible = useMemo(
        () => Boolean(recordUuid) && TIMELINE_KINDS_NEED.some((c) => components.includes(c)),
        [components, recordUuid],
    );

    useEffect(() => {
        if (!eligible || !recordUuid) return;
        let active = true;
        setLoading(true);
        (async () => {
            try {
                const messageRows = components.includes("MessageConversation") || components.includes("ScheduledActivity")
                    ? await runtime.services.chatter.listTimeline(modelKey, recordUuid, { pageSize: 100 })
                    : [];
                const attachmentRows = components.includes("Attachments")
                    ? await runtime.services.chatter.listAttachments(modelKey, recordUuid).catch(() => [])
                    : [];
                const mapped = [
                    ...messageRows.map(mapMessageRow),
                    ...attachmentRows.map(mapAttachmentRow),
                ];
                // Resolve raw author/owner uuids in one batch — top-level
                // entries AND nested reply authors (Enh 04).
                const authorBearers = mapped.flatMap((e) => [e, ...(e.replies ?? [])]);
                const labels = await resolveUserLabels(
                    runtime.services.record,
                    authorBearers.map((e) => (e.authorLabel === "System" ? null : e.authorLabel)),
                );
                for (const entry of authorBearers) {
                    const resolved = labels.get(entry.authorLabel);
                    if (resolved) entry.authorLabel = resolved;
                    else if (entry.authorLabel !== "System" && entry.authorLabel.length > 20) {
                        // unresolved bare uuid — keep it short
                        entry.authorLabel = `${entry.authorLabel.slice(0, 8)}…`;
                    }
                }
                mapped.sort((a, b) => (a.ts < b.ts ? 1 : a.ts > b.ts ? -1 : 0));
                if (active) setEntries(mapped);
                // Lazy image-card thumbnails (authenticated blob, capped).
                const imageFiles = mapped
                    .filter((e) => e.kind === "file" && e.attachmentUuid && isImage(e.mimetype ?? ""))
                    .slice(0, 12);
                for (const entry of imageFiles) {
                    const uuid = entry.attachmentUuid!;
                    if (thumbsRef.current[uuid]) continue;
                    runtime.services.chatter
                        .previewAttachment(uuid)
                        .then((blob) => {
                            if (!active) return;
                            const url = URL.createObjectURL(blob);
                            thumbsRef.current = { ...thumbsRef.current, [uuid]: url };
                            setThumbs(thumbsRef.current);
                        })
                        .catch(() => {
                            /* icon fallback */
                        });
                }
            } catch {
                if (active) setEntries([]);
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eligible, modelKey, recordUuid, epoch, refreshSignal]);

    // Live refresh: a save on THIS record may have produced tracking entries.
    useEvent("record.saved", (payload) => {
        if (payload.modelKey === modelKey && payload.recordUuid === recordUuid) {
            setEpoch((n) => n + 1);
        }
    });

    // Strip chip jump — apply the filter and bring the feed into view.
    useEffect(() => {
        if (!jumpSignal) return;
        setActiveFilter(jumpSignal.filter);
        rootRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jumpSignal?.n]);

    if (!eligible) return null;

    // Smart open (user-directed): previewable types open an in-place dialog
    // over the authenticated blob; everything else downloads. Never a new tab.
    const onFileOpen = async (entry: TimelineEntry) => {
        if (!entry.attachmentUuid) return;
        const name = entry.fileName ?? "file";
        try {
            if (isInlineViewable(entry.mimetype ?? "")) {
                const blob = await runtime.services.chatter.previewAttachment(entry.attachmentUuid);
                const url = URL.createObjectURL(blob);
                setPreview((prev) => {
                    if (prev) URL.revokeObjectURL(prev.url);
                    return { name, url, mimetype: entry.mimetype ?? "" };
                });
            } else {
                const blob = await runtime.services.chatter.downloadAttachment(entry.attachmentUuid);
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = name;
                a.click();
                setTimeout(() => URL.revokeObjectURL(url), 30_000);
            }
        } catch {
            /* non-fatal */
        }
    };

    const closePreview = () => {
        setPreview((p) => {
            if (p) URL.revokeObjectURL(p.url);
            return null;
        });
    };

    return (
        <div ref={rootRef} className="record-timeline__anchor">
            {/* Composer — its OWN section between the form body and the
                timeline card (user-directed layout). */}
            {components.includes("MessageConversation") && recordUuid ? (
                <div className="record-composer-section">
                    <h3 className="record-composer-section__title">
                        <MessageSquare size={13} aria-hidden="true" />
                        Post an update
                    </h3>
                    <RecordComposer
                        components={components}
                        fieldDefs={fieldDefs}
                        modelKey={modelKey}
                        recordUuid={recordUuid}
                        onPosted={() => {
                            // One shared epoch when the parent listens (it
                            // feeds refreshSignal back) — avoids double fetch.
                            if (onMutated) onMutated();
                            else setEpoch((n) => n + 1);
                        }}
                    />
                </div>
            ) : null}
            <RecordTimelineView
                components={components}
                entries={entries}
                thumbs={thumbs}
                loading={loading}
                activeFilter={activeFilter}
                onFilterChange={setActiveFilter}
                timezone={locale.timezone}
                onFileOpen={(entry) => void onFileOpen(entry)}
            />
            {/* In-place file preview dialog — same no-new-tab contract as the
                documents drawer. */}
            <Dialog.Root open={Boolean(preview)} onOpenChange={(open) => !open && closePreview()}>
                <Dialog.Portal>
                    <Dialog.Overlay className="theme-dialog__overlay" />
                    <Dialog.Content className="record-timeline__preview" aria-describedby={undefined}>
                        <header className="record-timeline__preview-head">
                            <Dialog.Title className="record-timeline__preview-title">{preview?.name}</Dialog.Title>
                            <button
                                type="button"
                                className="record-timeline__preview-btn"
                                onClick={() => {
                                    if (!preview) return;
                                    const a = document.createElement("a");
                                    a.href = preview.url;
                                    a.download = preview.name;
                                    a.click();
                                }}
                                aria-label="Download file"
                                title="Download"
                            >
                                <Download size={14} />
                            </button>
                            <Dialog.Close className="record-timeline__preview-btn" aria-label="Close preview">
                                <X size={15} />
                            </Dialog.Close>
                        </header>
                        <div className="record-timeline__preview-body">
                            {preview && isImage(preview.mimetype) ? (
                                <img src={preview.url} alt={preview.name} />
                            ) : preview ? (
                                <iframe src={preview.url} title={preview.name} />
                            ) : null}
                        </div>
                    </Dialog.Content>
                </Dialog.Portal>
            </Dialog.Root>
        </div>
    );
}
